import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon, LinkedinIcon } from 'lucide-react';
import { PageHero } from '../components/ui/PageHero';
import { Reveal } from '../components/ui/Reveal';
import { contactInfo } from '../data/navigation';
import { EASE_OUT } from '../utils/motion';
type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  consent: boolean;
};
type FormErrors = Partial<Record<keyof FormState, string>>;
type Status = 'idle' | 'submitting' | 'success';
const initialForm: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  subject: 'Optimisation des achats',
  message: '',
  consent: false
};
const subjects = ['Optimisation des achats', 'Gestion de la supply chain', 'Organisation & processus', 'Performance fournisseurs', 'Projet stratégique', 'Management de transition', 'Autre demande'];
const nextSteps = [{
  title: 'Votre message',
  text: 'Vous décrivez votre contexte en quelques lignes.'
}, {
  title: 'Un échange de 30 min',
  text: 'Je vous rappelle sous 24 h ouvrées pour comprendre vos enjeux.'
}, {
  title: 'Une proposition claire',
  text: 'Vous recevez une proposition chiffrée, sans engagement.'
}];
export function Contact() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const update = <K extends keyof FormState,>(key: K, value: FormState[K]) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = 'Merci d’indiquer votre nom.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Merci d’indiquer un email valide.';
    if (form.message.trim().length < 10) e.message = 'Décrivez votre besoin en quelques mots (10 caractères minimum).';
    if (!form.consent) e.consent = 'Votre accord est nécessaire pour vous recontacter.';
    return e;
  };
  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 1100);
  };
  const fieldClass = (hasError: boolean) => `mt-2 w-full rounded-xl bg-paper px-4 py-3 text-[15px] text-ink ring-1 ring-inset transition-shadow duration-150 placeholder:text-ink/40 focus:outline-none focus:ring-2 ${hasError ? 'ring-[#B4372B]' : 'ring-ink/10 focus:ring-ink'}`;
  return <>
      <PageHero label="Contact" title="Parlons de vos achats." description="Un projet, une question, un besoin urgent ? Décrivez votre situation : je vous réponds personnellement sous 24 heures ouvrées." data-id="element-566" data-mp="18enoj63ek4h7" />

      <section className="bg-paper py-20 lg:py-28" data-id="element-567" data-mp="2rewpttucgfzr">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12" data-id="element-568" data-mp="2rd24wfj2onvo">
          <Reveal className="lg:col-span-7" data-id="element-569" data-mp="2w64yuorrhzce">
            <div className="rounded-[28px] bg-white p-6 sm:p-10" data-id="element-570" data-mp="2hggypqvbh6q9">
              <AnimatePresence mode="wait" data-id="element-571" data-mp="2hia5r79udsys">
                {status === 'success' ? <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.96
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.3,
                ease: EASE_OUT
              }} className="py-16 text-center" role="status" data-id="element-572" data-mp="2mg3advvhyewy">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ink text-brass" data-id="element-573" data-mp="355rqiiuovqs9">
                      <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-574" data-mp="1porky5wzb6sr" />
                    </span>
                    <h2 className="mt-6 text-3xl font-medium tracking-tight" data-id="element-575" data-mp="1tre8humma5k5">Merci, {form.name.split(' ')[0]} !</h2>
                    <p className="mx-auto mt-3 max-w-md leading-relaxed text-muted" data-id="element-576" data-mp="29z2ir88v3on8">
                      Votre message est bien envoyé. Je reviens vers vous sous 24 heures ouvrées pour convenir d’un
                      premier échange.
                    </p>
                    <button type="button" onClick={() => {
                  setForm(initialForm);
                  setStatus('idle');
                }} className="mt-8 rounded-xl bg-mist px-5 py-3 text-sm font-medium transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-577" data-mp="1v94n678lrwo0">
                      Envoyer un autre message
                    </button>
                  </motion.div> : <motion.form key="form" exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} onSubmit={handleSubmit} noValidate data-id="element-578" data-mp="17k202jv79bm4">
                    <h2 className="text-2xl font-medium tracking-tight" data-id="element-579" data-mp="13h5k70x1ntlv">Votre demande</h2>
                    <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-580" data-mp="3f6bx58xoz3o7">
                      <div data-id="element-581" data-mp="3j6dx9dns3p1d">
                        <label htmlFor="name" className="text-sm font-medium" data-id="element-582" data-mp="3j7p0ncdbaury">Nom et prénom *</label>
                        <input id="name" value={form.name} onChange={e => update('name', e.target.value)} aria-invalid={!!errors.name} aria-describedby="name-error" className={fieldClass(!!errors.name)} autoComplete="name" data-id="element-583" data-mp="3o570md9cpznt" />
                        {errors.name && <p id="name-error" className="mt-1.5 text-sm text-[#B4372B]" data-id="element-584" data-mp="1sqjnlnuzfw3">{errors.name}</p>}
                      </div>
                      <div data-id="element-585" data-mp="aqkj35gx6pnh">
                        <label htmlFor="company" className="text-sm font-medium" data-id="element-586" data-mp="asdnsf7z2zsu">Entreprise</label>
                        <input id="company" value={form.company} onChange={e => update('company', e.target.value)} className={fieldClass(false)} autoComplete="organization" data-id="element-587" data-mp="fovsxg77t05p" />
                      </div>
                      <div data-id="element-588" data-mp="2hi21dboi1c5j">
                        <label htmlFor="email" className="text-sm font-medium" data-id="element-589" data-mp="2hjd4rae18hw4">Email professionnel *</label>
                        <input id="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={!!errors.email} aria-describedby="email-error" className={fieldClass(!!errors.email)} autoComplete="email" data-id="element-590" data-mp="3ftcpa8krsher" />
                        {errors.email && <p id="email-error" className="mt-1.5 text-sm text-[#B4372B]" data-id="element-591" data-mp="3oshozi82igsi">{errors.email}</p>}
                      </div>
                      <div data-id="element-592" data-mp="7jkzp8e1dgn5">
                        <label htmlFor="phone" className="text-sm font-medium" data-id="element-593" data-mp="2ncjrss0ljex">Téléphone</label>
                        <input id="phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className={fieldClass(false)} autoComplete="tel" data-id="element-594" data-mp="ger5469e9wp5" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-595" data-mp="qbvv6h6d2cb8">
                        <label htmlFor="subject" className="text-sm font-medium" data-id="element-596" data-mp="qab08qg4wrcu">Sujet</label>
                        <select id="subject" value={form.subject} onChange={e => update('subject', e.target.value)} className={fieldClass(false)} data-id="element-597" data-mp="lf5crn7334mn">
                          {subjects.map(s => <option key={s} data-id="element-598" data-mp="19w4hw8fagclp">{s}</option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-599" data-mp="zzhwnij1vjj5">
                        <label htmlFor="message" className="text-sm font-medium" data-id="element-600" data-mp="10244zkdrm32f">Votre besoin *</label>
                        <textarea id="message" rows={5} value={form.message} onChange={e => update('message', e.target.value)} aria-invalid={!!errors.message} aria-describedby="message-error" placeholder="Contexte, enjeux, calendrier…" className={`${fieldClass(!!errors.message)} resize-none`} data-id="element-601" data-mp="m6jmur5v1ow2" />
                        {errors.message && <p id="message-error" className="mt-1.5 text-sm text-[#B4372B]" data-id="element-602" data-mp="39qr8o980byk">{errors.message}</p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-603" data-mp="7blva0ijd3g1">
                        <label className="flex items-start gap-3 text-sm leading-relaxed text-muted" data-id="element-604" data-mp="7d6ljdxti083">
                          <input type="checkbox" checked={form.consent} onChange={e => update('consent', e.target.checked)} aria-invalid={!!errors.consent} className="mt-1 h-4 w-4 rounded accent-ink" data-id="element-605" data-mp="noigd17j4vdr" />
                          J’accepte que mes données soient utilisées pour être recontacté(e) au sujet de ma demande.
                        </label>
                        {errors.consent && <p className="mt-1.5 text-sm text-[#B4372B]" data-id="element-606" data-mp="4tbmk2zg390p">{errors.consent}</p>}
                      </div>
                    </div>
                    <button type="submit" disabled={status === 'submitting'} className="mt-8 inline-flex h-12 items-center gap-2 rounded-xl bg-ink px-6 text-[15px] font-medium text-white transition-colors duration-150 hover:bg-ink-700 disabled:opacity-70" data-id="element-607" data-mp="3r7brz8gqza5a">
                      {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-608" data-mp="3d9wwpn3tc85e" />}
                      {status === 'submitting' ? 'Envoi en cours…' : 'Envoyer ma demande'}
                    </button>
                  </motion.form>}
              </AnimatePresence>
            </div>
          </Reveal>

          <div className="lg:col-span-5" data-id="element-609" data-mp="26pareidywoz9">
            <Reveal delay={0.1} data-id="element-610" data-mp="26o838i8150n1">
              <h2 className="text-2xl font-medium tracking-tight" data-id="element-611" data-mp="26n6pg5zmq01m">Coordonnées</h2>
              <ul className="mt-6 space-y-5" data-id="element-612" data-mp="193lponyu093s">
                <li className="flex gap-4" data-id="element-613" data-mp="1e0c5da6q52oo">
                  <MailIcon className="mt-0.5 h-5 w-5 text-brass-dark" aria-hidden="true" data-id="element-614" data-mp="1e1eyzpvflj8a" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:underline" data-id="element-615" data-mp="1iydplyfvz81q">{contactInfo.email}</a>
                </li>
                <li className="flex gap-4" data-id="element-616" data-mp="1nshxql0w1m4e">
                  <PhoneIcon className="mt-0.5 h-5 w-5 text-brass-dark" aria-hidden="true" data-id="element-617" data-mp="qc2moh4t4v1r" />
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:underline" data-id="element-618" data-mp="v9m3nosw3lxe">{contactInfo.phone}</a>
                </li>
                <li className="flex gap-4" data-id="element-619" data-mp="oxc7rf6pxbir">
                  <MapPinIcon className="mt-0.5 h-5 w-5 text-brass-dark" aria-hidden="true" data-id="element-620" data-mp="tw5odghaxin5" />
                  <span data-id="element-621" data-mp="yw1kyuno67so">
                    {contactInfo.location}
                    <span className="block text-sm text-muted" data-id="element-622" data-mp="ytp6irh4efkd">{contactInfo.coverage}</span>
                  </span>
                </li>
                <li className="flex gap-4" data-id="element-623" data-mp="67mfayqfh1u0">
                  <LinkedinIcon className="mt-0.5 h-5 w-5 text-brass-dark" aria-hidden="true" data-id="element-624" data-mp="68xlt6b9cnot" />
                  <a href={contactInfo.linkedin} target="_blank" rel="noreferrer" className="hover:underline" data-id="element-625" data-mp="b6pei2jq8du2">Profil LinkedIn</a>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.2} className="mt-12 rounded-[28px] bg-ink p-8 text-white" data-id="element-626" data-mp="kwsbedtyybxo">
              <h2 className="text-xl font-medium" data-id="element-627" data-mp="3jkxumb52bc5c">Et ensuite ?</h2>
              <ol className="mt-6 space-y-6" data-id="element-628" data-mp="3oku3anopfrr8">
                {nextSteps.map((s, i) => <li key={s.title} className="flex gap-4" data-id="element-629" data-mp="25pzeap6dif8q" data-mpi={i}>
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-sm text-brass" data-id="element-630" data-mp="25p6ds5y0clz1">{i + 1}</span>
                    <div data-id="element-631" data-mp="1wpazhs95oaus">
                      <p className="font-medium" data-id="element-632" data-mp="1wohy76syudk1">{s.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-white/65" data-id="element-633" data-mp="1rq6oqx6l252c">{s.text}</p>
                    </div>
                  </li>)}
              </ol>
            </Reveal>
          </div>
        </div>
      </section>
    </>;
}